import { importGorgiasFaqs } from "@redotech/admin-sdk/rpc/schema/support/import-gorgias-faqs";
import { importGorgiasKnowledge } from "@redotech/admin-sdk/rpc/schema/support/import-gorgias-knowledge";
import { importGorgiasTags } from "@redotech/admin-sdk/rpc/schema/support/import-gorgias-tags";
import { importGorgiasViews } from "@redotech/admin-sdk/rpc/schema/support/import-gorgias-views";
import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import * as z from "zod/v4";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";

export enum GorgiasImportType {
  TAGS = "tags",
  FAQS = "faqs",
  VIEWS = "views",
}

interface UseGorgiasImportOptions {
  teamId: string | undefined;
  importType: GorgiasImportType;
  onSuccess?: () => void;
}

const TagsImportOptionsSchema = z.object({ jsonText: z.string() });

const FaqsImportOptionsSchema = z.object({
  jsonText: z.string(),
  createFaqPage: z.boolean(),
  publishImportedContent: z.boolean(),
});

type TagsImportOptions = z.infer<typeof TagsImportOptionsSchema>;
type FaqsImportOptions = z.infer<typeof FaqsImportOptionsSchema>;

export function useGorgiasImport({
  teamId,
  importType,
  onSuccess,
}: UseGorgiasImportOptions) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [jsonText, setJsonText] = useState("");

  const tagsMutation = useMutation({
    mutationFn: async ({ jsonText }: TagsImportOptions) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }
      const parsedJson = JSON.parse(jsonText);
      const gorgiasTagsJson =
        importGorgiasTags.input.shape.gorgiasTagsJson.parse(parsedJson);
      return rpcClient.importGorgiasTags({ teamId, gorgiasTagsJson });
    },
    onSuccess: (result) => {
      if (result.success) {
        toast(
          `Gorgias tags imported successfully: ${result.importedCount} imported, ${result.skippedCount} skipped`,
          { variant: "success" },
        );
        setJsonText("");
        onSuccess?.();
      } else {
        toast(
          `Import completed with errors: ${result.errors?.join(", ") || "Unknown error"}`,
          { variant: "error" },
        );
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Invalid JSON format";
      toast(`Failed to import: ${errorMessage}`, { variant: "error" });
    },
  });

  const faqsMutation = useMutation({
    mutationFn: async ({
      jsonText,
      createFaqPage,
      publishImportedContent,
    }: FaqsImportOptions) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }
      const parsedJson = JSON.parse(jsonText);
      const gorgiasFaqsJson =
        importGorgiasFaqs.input.shape.gorgiasFaqsJson.parse(parsedJson);
      return rpcClient.importGorgiasFaqs({
        teamId,
        gorgiasFaqsJson,
        createFaqPage,
        faqPageTitle: "Help Center",
        publishImportedContent,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        toast(
          `Gorgias FAQs imported: ${result.collectionsCreated} collections, ${result.articlesCreated} articles created. Skipped: ${result.collectionsSkipped} collections, ${result.articlesSkipped} articles.`,
          { variant: "success" },
        );
        setJsonText("");
        onSuccess?.();
      } else {
        toast(
          `Import completed with errors: ${result.errors?.join(", ") || "Unknown error"}`,
          { variant: "error" },
        );
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Invalid JSON format";
      toast(`Failed to import: ${errorMessage}`, { variant: "error" });
    },
  });

  const handleImport = (options: TagsImportOptions | FaqsImportOptions) => {
    if (!jsonText.trim() || !teamId) {
      return;
    }

    switch (importType) {
      case GorgiasImportType.TAGS: {
        const parsed = TagsImportOptionsSchema.safeParse(options);
        if (!parsed.success) {
          toast("Invalid tags import options", { variant: "error" });
          return;
        }
        tagsMutation.mutate(parsed.data);
        break;
      }
      case GorgiasImportType.FAQS: {
        const parsed = FaqsImportOptionsSchema.safeParse(options);
        if (!parsed.success) {
          toast("Invalid FAQs import options", { variant: "error" });
          return;
        }
        faqsMutation.mutate(parsed.data);
        break;
      }
    }
  };

  const loading = tagsMutation.isPending || faqsMutation.isPending;

  return { jsonText, setJsonText, loading, handleImport };
}

// Separate hook for views import with its own state management
interface UseGorgiasViewsImportOptions {
  teamId: string | undefined;
  onSuccess?: () => void;
}

export function useGorgiasViewsImport({
  teamId,
  onSuccess,
}: UseGorgiasViewsImportOptions) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [viewSectionsJsonText, setViewSectionsJsonText] = useState("");
  const [viewsJsonText, setViewsJsonText] = useState("");

  const viewsMutation = useMutation({
    mutationFn: async ({
      viewSectionsJsonText,
      viewsJsonText,
    }: {
      viewSectionsJsonText: string;
      viewsJsonText: string;
    }) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }
      const parsedSectionsJson = JSON.parse(viewSectionsJsonText);
      const parsedViewsJson = JSON.parse(viewsJsonText);
      const gorgiasViewSectionsJson =
        importGorgiasViews.input.shape.gorgiasViewSectionsJson.parse(
          parsedSectionsJson,
        );
      const gorgiasViewsJson =
        importGorgiasViews.input.shape.gorgiasViewsJson.parse(parsedViewsJson);
      return rpcClient.importGorgiasViews({
        teamId,
        gorgiasViewSectionsJson,
        gorgiasViewsJson,
      });
    },
    onSuccess: (result) => {
      if (result.success) {
        const parts = [
          "Gorgias views imported:",
          `${result.foldersCreated} folders created`,
          `${result.viewsCreated} views created`,
        ];
        if (result.foldersSkipped > 0 || result.viewsSkipped > 0) {
          parts.push(
            `(${result.foldersSkipped} folders, ${result.viewsSkipped} views skipped)`,
          );
        }
        toast(parts.join(" "), { variant: "success" });
        setViewSectionsJsonText("");
        setViewsJsonText("");
        onSuccess?.();

        if (result.warnings.length > 0) {
          toast(`Warnings: ${result.warnings.slice(0, 3).join("; ")}`, {
            variant: "warning",
          });
        }
      } else {
        toast(
          `Import completed with errors: ${result.errors.join(", ") || "Unknown error"}`,
          { variant: "error" },
        );
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Invalid JSON format";
      toast(`Failed to import: ${errorMessage}`, { variant: "error" });
    },
  });

  const handleImport = () => {
    if (!viewSectionsJsonText.trim() || !viewsJsonText.trim() || !teamId) {
      return;
    }
    viewsMutation.mutate({ viewSectionsJsonText, viewsJsonText });
  };

  return {
    viewSectionsJsonText,
    setViewSectionsJsonText,
    viewsJsonText,
    setViewsJsonText,
    loading: viewsMutation.isPending,
    handleImport,
  };
}

interface UseGorgiasKnowledgeImportOptions {
  teamId: string | undefined;
  onSuccess?: () => void;
}

export function useGorgiasKnowledgeImport({
  teamId,
  onSuccess,
}: UseGorgiasKnowledgeImportOptions) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [jsonText, setJsonText] = useState("");

  const knowledgeMutation = useMutation({
    mutationFn: async ({ jsonText }: { jsonText: string }) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }
      const parsedJson = JSON.parse(jsonText);
      const gorgiasKnowledgeJson =
        importGorgiasKnowledge.input.shape.gorgiasKnowledgeJson.parse(
          parsedJson,
        );
      return rpcClient.importGorgiasKnowledge({ teamId, gorgiasKnowledgeJson });
    },
    onSuccess: (result) => {
      if (result.success) {
        toast(
          `Gorgias knowledge imported: ${result.knowledgeCreated} created, ${result.knowledgeSkipped} skipped`,
          { variant: "success" },
        );
        setJsonText("");
        onSuccess?.();
      } else {
        toast(
          `Import completed with errors: ${result.errors?.join(", ") || "Unknown error"}`,
          { variant: "error" },
        );
      }
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Invalid JSON format";
      toast(`Failed to import: ${errorMessage}`, { variant: "error" });
    },
  });

  const handleImport = () => {
    if (!jsonText.trim() || !teamId) {
      return;
    }
    knowledgeMutation.mutate({ jsonText });
  };

  return {
    jsonText,
    setJsonText,
    loading: knowledgeMutation.isPending,
    handleImport,
  };
}
